import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  OnInit,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
} from 'rxjs/operators';

//INTERFACE
import { EFeatureFlag } from '@app/feature-toggle-list';

import { IBom, IBomRfq } from '../../../bom/interfaces/bom.interface';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';
import { IGetApiResponse } from '../../../shared/interfaces/api-response.interface';

//SERVICES
import { BomApiService } from '../../../shared/services/api/bom-api.service';
import { RfqApiService } from '../../../shared/services/api/rfq/rfq-api.service';
import { FeatureFlagUtilService } from '../../../shared/services/util/feature-flag-util.service';

// CONFIGS
import { trackByValue } from '../../../shared/util/track-by.util';

@Component({
  selector: 'lum-rfq-assign-bom-form',
  templateUrl: './rfq-assign-bom-form.component.html',
  styleUrls: ['./rfq-assign-bom-form.component.scss'],
})
export class RfqAssignBomFormComponent implements OnInit {
  boms$ :Observable<any>;
  boms :IBom[] = [];
  selectedBoms :IBom[] = [];
  inputValue :string = '';
  disableInput :boolean = false;
  private searchUpdated$ = new BehaviorSubject<string>('');
  private onDestroy$ = new Subject();
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _apiResponseData = new BehaviorSubject<IGetApiResponse>(
    <IGetApiResponse>{},
  );
  loading$ = this._loading.asObservable();
  apiResponseData$ = this._apiResponseData.asObservable();
  trackByValue = trackByValue;

  @Output()
  bomIdEvent = new EventEmitter<number>();

  @ViewChild('autocompleteInput') autocompleteInput :ElementRef;
  constructor(
    @Inject(MAT_DIALOG_DATA)
    private data :{
      rfqId :number;
      bomRfqs :IBomRfq[];
    },
    private bomApiService :BomApiService,
    private snackBar :MatSnackBar,
    private rfqApiService :RfqApiService,
    @Optional() public dialogRef :MatDialogRef<RfqAssignBomFormComponent>,
    private featureFlagUtilService :FeatureFlagUtilService,
  ) {}

  ngOnInit() :void {
    this.initSelectedBoms();
    this.onSearchUpdate();
  }

  onSubmit() :void {
    const rfqId = this.data?.rfqId;
    const payload = {
      bomIds: this.selectedBoms?.map((bom :IBom) => bom.id) ?? [],
    };
    if (rfqId) {
      this._loading.next(true);
      this.rfqApiService.syncBomsRfq(rfqId, payload).subscribe({
        next: (response :any) => {
          this.snackBar.open('Successfully synced BOMs', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this._loading.next(false);
          const dataResponse = response ?? [];
          this.dialogRef.close(dataResponse);
        },
        error: (responseError :any) => {
          this.snackBar.open(
            responseError?.error ? responseError.error : 'Error updating record',
            'Close',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
          this._loading.next(false);
        },
      });
    }
  }

  onSearchKeywordChange(value :string) :void {
    this._loading.next(true);
    this.searchUpdated$.next(value);
    this.disableInput = true;
  }

  private onSearchUpdate() :void {
    this._loading.next(true);

    this.searchUpdated$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((searchValue) =>
          this.bomApiService.get({ name: searchValue, limit: '1000' }).pipe(
            catchError((error) => {
              this.snackBar.open(
                error?.error ? error.error : 'Error searching record',
                'Close',
                { duration: SnackBarConfig.DEFAULT.duration },
              );

              return of(this.boms); // return the current boms array on error
            }),
          ),
        ),
        map((response) => response || []),
        finalize(() => this._loading.next(false)), // Ensure loading is turned off after completion or error
      )
      .subscribe((response) => {
        this.boms = response.data;
        this._loading.next(false);
      });
  }

  onSelectOption(event :MatAutocompleteSelectedEvent) :void {
    this.selectedBoms.push(event.option.value);
    this.autocompleteInput.nativeElement.value = '';
    this.disableInput = false;
  }

  onRemove(bom :IBom) :void {
    this.selectedBoms = this.selectedBoms.filter(
      (selectedBom :IBom) => selectedBom.id !== bom.id,
    );
  }

  private initSelectedBoms() :void {
    if (this.data.bomRfqs.length > 0) {
      this.selectedBoms = this.data.bomRfqs.map((bomRfq :IBomRfq) => bomRfq.bom);
    }
  }

  get featureBOMAssemblyOrdersEnabled() :boolean {
    return this.featureFlagUtilService.isFeatureEnabled(
      EFeatureFlag.ASSEMBLY_ORDER,
    );
  }

  get uniqueBoms() :IBom[] {
    return this.boms.filter(
      (bom :IBom) =>
        !this.selectedBoms.some((selectedBom :IBom) => selectedBom.id === bom.id),
    );
  }
}
